import styled, { css, keyframes } from 'styled-components';
import {
  Button,
  Divider,
  Flex,
  Icon,
  isNotDarkThemeMixin,
  Link,
  styleMixinFocusOutline,
  TextButton,
} from '@amzn/storm-ui';
import { orange } from 'src/colors';

type ThemedProps = { theme: { spacing: { base: { default: string } } } };
const accordionLeftIndent = ({ theme }: ThemedProps): string =>
  theme.spacing.base.default;

type SubGroupThemedProps = {
  theme: { spacing: { xxsmall: { default: string } } };
};
const subGroupTitleInlineStart = ({ theme }: SubGroupThemedProps): string =>
  theme.spacing.xxsmall.default;

const subGroupRailInlineStart = (props: SubGroupThemedProps): string =>
  `calc(${subGroupTitleInlineStart(props)} + 1px)`;

export const DATA_HAS_NEXT_GROUP = 'data-has-next-group';
export const DATA_CONTINUATION_BLOCK = 'data-continuation-block';

const easeCurve = `cubic-bezier(0.2, 0, 0.38, 0.9)`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 100%;
  }
`;

const reducedMotionMediaQuery = css`
  @media screen and (prefers-reduced-motion: reduce) {
    & {
      transition: none !important;
    }
  }
`;

export const HiddenContent = styled(Flex)`
  opacity: 0;
  width: 100%;
`;

export const AllToolsHiddenContent = styled.div<{ width: string }>`
  display: flex; // TODO: use Flex component from StormUI
  flex-direction: column;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 0 ${({ theme }) => theme.spacing.small.default};
  padding-inline: ${({ theme }) => theme.spacing.small.default};
  padding-block: ${({ theme }) => theme.spacing.small.default};
  max-height: 100%;
  overflow: auto;
  min-width: ${({ width }) =>
    width} !important; // TODO: increase selector's specificity instead of using !important
  pointer-events: none;
  opacity: 0;
  width: 0;
  overflow: hidden;
`;

export const ToolbarWrapper = styled.div<{ isNavOpen: boolean }>`
  background-color: ${({ theme }) => theme.surface.container.default};
  flex: 1;
  min-height: 0;
  z-index: 300;
  display: flex;
  flex-direction: column;

  ${({ isNavOpen }) =>
    isNavOpen &&
    css`
      & ${HiddenContent} {
        opacity: 1;
        animation: ${fadeIn} 0.15s linear;
      }
    `}
`;

export const NavItemsContainer = styled(Flex).attrs({
  direction: 'column',
})`
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
`;

export const ToolbarOutterWrapper = styled.div<{
  isNavOpen: boolean;
  isAllToolsOpen: boolean;
}>`
  display: flex; // TODO: use Flex component instead
  background-color: ${({ theme }) => theme.surface.container.default};
  border-inline-end: 1px solid
    ${({ theme }) => theme.elevation.borderColor.subtle};
  padding: ${({ theme }) => theme.spacing.xsmall.default};
  box-sizing: border-box;
  overflow: hidden;
  height: 100%;
  width: 64px;
  z-index: 300;
  max-width: 100%;

  &:hover {
    overflow: hidden auto;
  }

  ${isNotDarkThemeMixin(css`
    box-shadow: ${({ theme }) => theme.elevation.shadow.panel};
  `)};

  ${reducedMotionMediaQuery};
  -webkit-transition: all 100ms linear;
  -moz-transition: all 100ms linear;
  -o-transition: all 100ms linear;
  -ms-transition: all 100ms linear;
  transition: all 100ms linear;

  ${({ isNavOpen }) =>
    isNavOpen &&
    css`
      width: 320px;

      & ${HiddenContent} {
        opacity: 1;
        animation: ${fadeIn} 0.15s linear;
      }
    `}

  ${({ isAllToolsOpen }) =>
    isAllToolsOpen &&
    css`
      width: 100%;
      overflow: auto;

      &:hover {
        overflow: auto;
      }

      & ${AllToolsHiddenContent} {
        opacity: 1;
        pointer-events: auto;
        width: 100%;
        animation: ${fadeIn} 0.15s linear;
      }
    `}
`;

export const Overlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 64px;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.nav.overlay.backgroundColor};
  opacity: 10%;
  z-index: 51;
`;

const SidenavIndicator = css`
  content: '';
  height: 24px;
  width: 3px;
  position: absolute;
  top: 8px;
  left: -12px;
  border-radius: 0
    ${({ theme }) =>
      `${theme.border.radius.small} ${theme.border.radius.small}`}
    0;
`;

const SelectedSectionIndicator = css`
  ::before {
    ${SidenavIndicator};
    background-color: ${orange};
  }
`;

const HoverSectionIndicator = css`
  ::before {
    ${SidenavIndicator};
    background-color: ${({ theme }) => theme.color.gray[600]};
  }
`;

export const SideNavLinkOuterWrapper = styled.div<{ isSelected: boolean }>`
  min-height: 40px;
  width: 296px;
  cursor: pointer;
  margin-bottom: ${({ theme }) => theme.spacing.xsmall.default};
  position: relative;
  box-sizing: border-box;

  ${({ isSelected }) =>
    isSelected
      ? SelectedSectionIndicator
      : css`
          :hover {
            ${HoverSectionIndicator};
          }
        `};
`;

const sideNavLinkStyles = css<{ isSelected: boolean; isNavOpen: boolean }>`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  border-radius: ${({ theme }) => theme.border.radius.large};
  cursor: pointer;
  border: none;
  background-color: ${({ theme }) =>
    theme.selectableContainer.backgroundColor.default};
  min-height: 40px;
  padding-inline: ${({ theme }) =>
    `${theme.spacing.xxsmall.default} ${theme.spacing.xsmall.default}`};

  &&& {
    text-decoration: none;
    color: ${({ theme }) => theme.text.color.default};
  }

  && [id^='aac-set-landing-nav'],
  && [id^='aac-unset-landing-nav'] {
    margin-inline-end: -${({ theme }) => theme.spacing.xsmall.default};
  }

  && [id^='aac-set-landing-nav'] {
    opacity: 0;
    pointer-events: none;
    position: absolute;
  }

  ${({ isNavOpen }) =>
    isNavOpen &&
    css`
      :hover,
      :focus-visible {
        background-color: ${({ theme }) =>
          theme.selectableContainer.backgroundColor.hover};

        && [id^='aac-set-landing-nav'] {
          opacity: 1;
          pointer-events: auto;
          position: relative;
        }
      }

      && [id^='aac-set-landing-nav']:focus-visible {
        opacity: 1;
        pointer-events: auto;
        position: relative;
      }
    `}
`;

const focusOutlineMixin = css<{ isNavOpen: boolean }>`
  ${({ isNavOpen }) =>
    isNavOpen
      ? css`
          &:focus-visible {
            ${styleMixinFocusOutline};
            outline-offset: -2px;
          }
        `
      : css`
          &:focus-visible {
            outline: none;
          }
        `};
`;

export const SideNavLinkInnerWrapper = styled(Link)<{
  isSelected: boolean;
  isNavOpen: boolean;
}>`
  ${sideNavLinkStyles};

  &:link,
  &:visited {
    color: ${({ theme }) => theme.text.color.default};
    text-decoration: none;
  }

  ${({ isSelected, isNavOpen, theme }) =>
    isSelected &&
    isNavOpen &&
    css`
      background-color: ${theme.selectableContainer.backgroundColor.hover};
      font-weight: 700;
    `};

  ${focusOutlineMixin};
`;

export const SideNavButtonInnerWrapper = styled(TextButton)`
  ${sideNavLinkStyles};
  ${focusOutlineMixin};
  &:hover,
  &:active,
  &:focus-visible {
    text-decoration: none;
    color: ${({ theme }) => theme.color.gray[800]};
  }
`;

export const SideNavLinkTitleWrapper = styled.span<{
  isAllToolsOpen?: boolean;
  isFromAllTools?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: ${({ isFromAllTools, isAllToolsOpen }) => (isFromAllTools && isAllToolsOpen ? 'bold' : 'normal')};
  transition: font-weight 150ms ease;
  }
`;

export const SvgWrapper = styled(Flex)<{ isSelected: boolean }>`
  box-sizing: unset;
  cursor: pointer;
  margin-inline-end: ${({ theme }) => theme.spacing.small.default};
`;

export const AccordionWrapper = styled.div<{ isActive: boolean }>`
  width: 296px;
  margin-bottom: ${({ theme }) => theme.spacing.xsmall.default};
  &[${DATA_HAS_NEXT_GROUP}] {
    margin-bottom: 0;
  }
  &[${DATA_CONTINUATION_BLOCK}] > *:first-child {
    margin-block-start: 0;
  }
  ${AllToolsHiddenContent} &:not([${DATA_HAS_NEXT_GROUP}]) {
    margin-bottom: ${({ theme }) => theme.spacing.small.default};
  }
`;

export const AccordionButtonOuterWrapper = styled.div<{
  isSelected: boolean;
}>`
  position: relative;

  ${({ isSelected }) =>
    isSelected
      ? SelectedSectionIndicator
      : css`
          :hover {
            ${HoverSectionIndicator};
          }
        `};
`;

export const AccordionButtonInnerWrapper = styled.button<{
  isSelected: boolean;
  isNavOpen: boolean;
}>`
  ${sideNavLinkStyles};
  text-align: initial;

  ${({ isSelected }) =>
    isSelected &&
    css`
      font-weight: 700;
    `};

  ${focusOutlineMixin};
`;

export const AccordionDropdownIcon = styled(Icon)`
  margin-inline-start: auto;
`;

const gridRowsCollapsibleMixin = css`
  display: grid;
  overflow: hidden;
  ${reducedMotionMediaQuery};
`;

export const AccordionContentOuterWrapper = styled.div<{
  isActive: boolean;
  isNavOpen: boolean;
}>`
  ${gridRowsCollapsibleMixin};
  grid-template-rows: minmax(0, 0fr);
  padding-inline-start: 19px;
  transition: grid-template-rows 0.1s ${easeCurve};

  ${({ isActive, isNavOpen }) =>
    isActive &&
    isNavOpen &&
    css`
      grid-template-rows: minmax(0, 1fr);
      transition: grid-template-rows 0.25s ${easeCurve};
    `}
`;

export const AccordionContentInnerWrapper = styled.div<{ isActive: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  padding-top: ${({ theme }) => theme.spacing.xsmall.default};
  padding-inline-start: ${accordionLeftIndent};
  min-height: 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset-block-start: ${({ theme }) => theme.spacing.xsmall.default};
    inset-block-end: 0;
    inset-inline-start: 0;
    width: 2px;
    background-color: ${({ isActive, theme }) =>
      isActive ? theme.border.color.subtle.default : 'transparent'};
  }
`;

export const AccordionContentGroupWrapper = styled(HiddenContent)`
  box-sizing: border-box;
`;

export const AccordionContentSubTitle = styled.span`
  color: ${({ theme }) => theme.text.color.default};

  &&& {
    font-size: 12px;
  }

  font-weight: bold;
  line-height: 1.333;
  padding: ${({ theme }) =>
    `${theme.spacing.xxsmall.default} 0 ${theme.spacing.xxxsmall.default} ${theme.spacing.xxsmall.default}`};
  margin-bottom: ${({ theme }) => theme.spacing.xxsmall.default};
`;

export const SubGroupToggleButton = styled(Button)`
  && {
    width: 100%;
    justify-content: space-between;
    padding-block: ${({ theme }) => theme.spacing.xxsmall.default};
    padding-inline-start: ${subGroupTitleInlineStart};
    padding-inline-end: ${({ theme }) => theme.spacing.xsmall.default};
    margin: 0;
    text-align: start;
    color: ${({ theme }) => theme.text.color.default};
  }

  &&:hover {
    background-color: ${({ theme }) =>
      theme.selectableContainer.backgroundColor.hover};
  }
`;

export const SubGroupCollapseWrapper = styled.div<{ isExpanded: boolean }>`
  ${gridRowsCollapsibleMixin};
  width: 100%;
  grid-template-rows: minmax(0, 0fr);
  transition: grid-template-rows 0.1s ${easeCurve};

  ${({ isExpanded }) =>
    isExpanded &&
    css`
      grid-template-rows: minmax(0, 1fr);
      transition: grid-template-rows 0.25s ${easeCurve};
    `}
`;

// Inner child with min-height: 0 required for the grid-rows animation to collapse to zero.
export const SubGroupCollapseInner = styled.div`
  min-height: 0;
  overflow: hidden;
  position: relative;
  padding-inline-start: ${({ theme }) => theme.spacing.base.default};

  &::before {
    content: '';
    position: absolute;
    inset-block-start: 0;
    inset-block-end: 0;
    inset-inline-start: ${subGroupRailInlineStart};
    width: 2px;
    background-color: ${({ theme }) => theme.border.color.subtle.default};
    z-index: 1;
  }
`;

export const AccordionLinkWrapper = styled.div<{
  isSelected: boolean;
  isNavOpen: boolean;
}>`
  ${sideNavLinkStyles};
  padding-inline-start: 0;

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      background-color: ${theme.selectableContainer.backgroundColor.selected};
    `};
`;

export const AccordionLink = styled(Link)<{
  isSelected: boolean;
}>`
  padding: ${({ theme }) => theme.spacing.xxsmall.default};
  padding-inline-start: ${({ theme }) => theme.spacing.xxsmall.default};
  border-radius: ${({ theme }) => theme.border.radius.large};
  flex-grow: 1;

  &&& {
    text-decoration: none;
    color: ${({ theme }) => theme.text.color.default};

  &:link,
  &:visited {
    color: ${({ theme }) => theme.text.color.default};
    text-decoration: none;
  }

  ${({ isSelected }) => isSelected && `font-weight: 700;`};

  &&&:focus-visible {
    background-color: ${({ theme }) =>
      theme.selectableContainer.backgroundColor.default};
  }
`;

export const StyledDivider = styled(Divider)`
  margin: 5px 12px;
`;
